import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import assistantAvatar from '../../assets/assistant-avatar.png';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { HandThumbUpIcon, HandThumbDownIcon } from '@heroicons/react/24/outline';
import LoadingDots from './LoadingDots';
import { chatService } from '../../services/api';
import FeedbackModal from '../feedback/FeedbackModal';
import { useLanguage } from '../../context/LanguageContext';

const ChatMessage = ({ message, isAuthenticated, conversationId, feedbackStatus }) => {
  const { t } = useLanguage();
  const isAssistant = message.role === 'assistant';
  const [showReasoning, setShowReasoning] = useState(false);
  const [isSendingFeedback, setSendingFeedback] = useState(false);
  const [positiveFeedbackSent, setPositiveFeedbackSent] = useState(feedbackStatus === true);
  const [negativeFeedbackSent, setNegativeFeedbackSent] = useState(feedbackStatus === false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  useEffect(() => {
    setPositiveFeedbackSent(feedbackStatus === true);
    setNegativeFeedbackSent(feedbackStatus === false);
  }, [feedbackStatus]);

  const renderReasoningList = (reasoning) => {
    if (!reasoning) return null;
    return reasoning.split('\n')
      .map((line, index) => line.trim())
      .filter(line => line.length > 0)
      .map((line, index) => (
        <p key={index} className="leading-normal">
          {line}
        </p>
      ));
  };

  const handlePositiveFeedback = async () => {
    if (isSendingFeedback || positiveFeedbackSent || negativeFeedbackSent) return;
    
    try {
      setSendingFeedback(true);
      await chatService.sendFeedback(
        message.message_id,
        conversationId,
        true
      );
      setPositiveFeedbackSent(true);
    } catch (error) {
      console.error('Error sending positive feedback:', error);
    } finally {
      setSendingFeedback(false);
    }
  };

  const handleNegativeFeedback = () => {
    if (isSendingFeedback || positiveFeedbackSent || negativeFeedbackSent) return;
    setShowFeedbackModal(true);
  };

  const handleFeedbackSubmit = async ({ feedbackType, feedbackText }) => {
    try {
      setSendingFeedback(true);
      await chatService.sendFeedback(
        message.message_id,
        conversationId,
        false,
        feedbackType,
        feedbackText
      );
      setNegativeFeedbackSent(true);
      setShowFeedbackModal(false);
    } catch (error) {
      console.error('Error sending negative feedback:', error);
    } finally {
      setSendingFeedback(false);
    }
  };

  return (
    <div className={`flex ${isAssistant ? '' : 'flex-row-reverse'} items-start gap-2.5 p-4`}>
      {isAssistant && (
        <img className="w-8 h-8 rounded-full" src={assistantAvatar} alt="Maverick AI avatar" />
      )}
      <div className={`flex flex-col ${isAssistant ? 'items-start' : 'items-end'}`}>
        {isAssistant && (
          <div className={`flex items-center space-x-2 rtl:space-x-reverse mb-1`}>
            <span className="text-sm font-semibold text-gray-700">
              Maverick AI
            </span>
          </div>
        )}
        <div className={`flex flex-col leading-1.5 p-4 border-gray-200 ${
          isAssistant ? 'bg-gray-100 rounded-ss-xl md:w-[50vw] w-[75vw] min-w-[20vw]' : 'bg-blue-500 text-white rounded-se-xl'
        } rounded-e-xl rounded-es-xl min-h-[60px]`}>
          <div className="min-h-[24px] overflow-x-auto break-words">
            {message.isLoading ? (
              <LoadingDots />
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                className="prose prose-invert max-w-none [&>p:last-child]:mb-0 [&>ul]:list-disc [&>ul]:pl-4 [&>ul]:mb-4 [&>ol]:list-decimal [&>ol]:pl-4 [&>ol]:mb-4 [&>p]:mb-4 [&>p:last-child]:mb-0 break-words overflow-x-auto"
                components={{
                  a: ({ node, ...props }) => (
                    <a {...props} className="text-blue-400 hover:text-blue-300 break-words" target="_blank" rel="noopener noreferrer" />
                  ),
                  p: ({ node, ...props }) => (
                    <p {...props} className="break-words" />
                  ),
                  code: ({ node, ...props }) => (
                    <code {...props} className="break-words bg-gray-700 rounded px-1" />
                  )
                }}
                breaks={true}
              >
                {message.content}
              </ReactMarkdown>
            )}
          </div>
          {isAssistant && Array.isArray(message.sources) && message.sources.length > 0 && isAuthenticated && (
            <div className="mt-3 pt-2 border-t border-gray-300">
              <p className="text-sm font-semibold text-gray-600 mb-1">{t('sources')}:</p>
              <ul className="text-sm text-gray-500 list-disc pl-4 space-y-1">
                {message.sources.map((source, index) => (
                  <li key={index} className="leading-tight">{source}</li>
                ))}
              </ul>
            </div>
          )}
          {isAssistant && message.reasoning && isAuthenticated && (
            <div className="mt-3 pt-2 border-t border-gray-300">
              <button
                onClick={() => setShowReasoning(!showReasoning)}
                className="flex items-center text-sm font-semibold text-gray-600 hover:text-gray-800 transition-colors"
              >
                <span>{t('reasoning')}</span>
                {showReasoning ? (
                  <ChevronUpIcon className="w-4 h-4 ml-1" />
                ) : (
                  <ChevronDownIcon className="w-4 h-4 ml-1" />
                )}
              </button>
              {showReasoning && (
                <div className="mt-2 text-sm text-gray-500 space-y-1">
                  {renderReasoningList(message.reasoning)}
                </div>
              )}
            </div>
          )}
          {isAssistant && message.debug && isAuthenticated && (
            <div className="mt-3 pt-2 border-t border-gray-300">
              <div className="text-sm text-gray-600">
                <p className="font-semibold mb-1">{t('debugInfo')}:</p>
                {message.debug.product_classification && (
                  <div className="mb-2">
                    {message.debug.product_classification.map((product, index) => (
                      <p key={index} className="text-gray-500">
                        {t('product')}: "{product.identifier}" ({t('confidence')}: {(product.score * 100).toFixed(1)}%)
                      </p>
                    ))}
                  </div>
                )}
                {message.debug.vector_classification && (
                  <div>
                    <p className="text-gray-500">
                      {t('vectorStore')}: {message.debug.vector_classification.needs_deep_knowledge ? t('yes') : t('no')}
                      {message.debug.vector_classification.reason && (
                        <span className="block ml-2">{t('reason')}: {message.debug.vector_classification.reason}</span>
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          {isAssistant && !message.isLoading && isAuthenticated && 
           !message.content.includes("Hey! I'm Maverick AI") && (
            <div className="flex items-center mt-2 space-x-2 h-6">
              <button
                onClick={handlePositiveFeedback}
                disabled={isSendingFeedback || positiveFeedbackSent || negativeFeedbackSent}
                className={`inline-flex items-center justify-center p-1 rounded-full transition-colors h-6 w-6 ${
                  positiveFeedbackSent 
                    ? 'text-green-500' 
                    : isSendingFeedback || negativeFeedbackSent
                      ? 'text-gray-300'
                      : 'text-gray-500 hover:text-green-500'
                }`}
              >
                <div className="relative flex items-center justify-center w-4 h-4">
                  <HandThumbUpIcon className="w-4 h-4" />
                  {isSendingFeedback && !positiveFeedbackSent && (
                    <div className="absolute left-full ml-1">
                      <LoadingDots />
                    </div>
                  )}
                </div>
              </button>
              <button
                onClick={handleNegativeFeedback}
                disabled={isSendingFeedback || positiveFeedbackSent || negativeFeedbackSent}
                className={`inline-flex items-center justify-center p-1 rounded-full transition-colors h-6 w-6 ${
                  negativeFeedbackSent 
                    ? 'text-red-500'
                    : isSendingFeedback || positiveFeedbackSent
                      ? 'text-gray-300'
                      : 'text-gray-500 hover:text-red-500'
                }`}
              >
                <div className="relative flex items-center justify-center w-4 h-4">
                  <HandThumbDownIcon className="w-4 h-4" />
                  {isSendingFeedback && !negativeFeedbackSent && (
                    <div className="absolute left-full ml-1">
                      <LoadingDots />
                    </div>
                  )}
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
      {showFeedbackModal && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          onSubmit={handleFeedbackSubmit}
        />
      )}
    </div>
  );
};

export default ChatMessage;
